import { ProcessableOrder } from "../interfaces/order";
import { Vendor, VendorResponse } from "../interfaces/vendor";
import { fetchWithAuth } from "./fetchWithAuth";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getVendors = async (): Promise<Vendor[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(`${apiBaseUrl}/api/Vendor/GetVendors`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Vendor Fetch Error:", errorText);
    throw new Error("Failed to fetch vendors");
  }

  const vendors: Vendor[] = await response.json();
  return vendors;
};

export const getVendor = async (vendorID: number): Promise<Vendor> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Vendor/GetVendor/${vendorID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Vendor ID ${vendorID} was not found.`);
    }

    const errorText = await response.text();
    console.error("Vendor Fetch Error:", errorText);
    throw new Error("Failed to fetch vendor");
  }

  const vendor: Vendor = await response.json();

  return vendor;
};

export const updateVendor = async (vendor: Vendor): Promise<Vendor> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Vendor/UpdateVendor`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(vendor),
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Vendor ID ${vendor.vendorID} was not found to update.`);
    }

    const errorText = await response.text();
    console.error("Vendor Update Error:", errorText);
    throw new Error("Failed to update the vendor.");
  }

  const updatedVendor: Vendor = await response.json();
  return updatedVendor;
};

export const getProcessableOrders = async (
  vendorID: number,
): Promise<ProcessableOrder[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Vendor/GetProcessableOrders/${vendorID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Processable Orders Fetch Error:", errorText);
    throw new Error("Failed to fetch processable orders");
  }

  const orders: ProcessableOrder[] = await response.json();
  return orders;
};

export const processOrdersSterling = async (
  criminalDetailIDs: number[],
  subscriptionDetailIDs: number[],
): Promise<{ success: boolean; details: string[] }> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const processResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/Vendor/ProcessOrdersSterling`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ criminalDetailIDs, subscriptionDetailIDs }),
    },
  );

  if (!processResponse.ok) {
    const errorText = await processResponse.text();
    console.log("error", errorText);
    throw new Error(errorText || "Sterling API request failed");
  }

  const result = await processResponse.json();
  return result;
};

export const processOrdersOSI = async (
  vendorID: number,
  criminalDetailIDs: number[],
  subscriptionDetailIDs: number[],
): Promise<Blob | null> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const processResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/Vendor/ProcessOrdersOSI?vendorID=${vendorID}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ criminalDetailIDs, subscriptionDetailIDs }),
    },
  );

  if (!processResponse.ok) {
    console.error("Failed to process orders for vendor.");
    return null;
  }

  const result = await processResponse.json();

  if (result.success && result.pdfBase64) {
    // Convert Base64 to Blob URL
    const byteCharacters = atob(result.pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    return blob;
  } else {
    console.error("Error from server:", result.message);
    return null;
  }
};

export const processOrdersFama = async (
  criminalDetailIDs: number[],
  subscriptionDetailIDs: number[],
): Promise<{ success: boolean; details: string[] }> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const processResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/Vendor/ProcessOrdersFama`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ criminalDetailIDs, subscriptionDetailIDs }),
    },
  );

  if (!processResponse.ok) {
    const errorText = await processResponse.text();
    console.log("error", errorText);
    throw new Error(errorText || "FAMA API request failed");
  }

  const result = await processResponse.json();
  return result;
};

export const processOrdersInformData = async (
  criminalDetailIDs: number[],
  subscriptionDetailIDs: number[],
): Promise<VendorResponse> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const processResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/Vendor/ProcessOrdersInformData`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ criminalDetailIDs, subscriptionDetailIDs }),
    },
  );

  if (!processResponse.ok) {
    const errorText = await processResponse.text();
    console.log("error", errorText);
    throw new Error(errorText || "InformData API request failed");
  }

  const result = await processResponse.json();
  return result;
};

export const initiateOrdersOSI = async (
  vendorID: number,
  criminalDetailIDs: number[],
  subscriptionDetailIDs: number[],
): Promise<boolean> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const processResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/Vendor/InitiateOrdersOSI?vendorID=${vendorID}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ criminalDetailIDs, subscriptionDetailIDs }),
    },
  );

  if (!processResponse.ok) {
    console.error("Failed to initiate orders.");
    return false;
  }

  return true;
};

export const initiateOrdersOmni = async (
  criminalDetailIDs: number[],
  subscriptionDetailIDs: number[],
) => {
  try {
    if (!apiBaseUrl) {
      throw new Error("API URL is not defined");
    }

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Vendor/InitiateOrdersOmni`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ criminalDetailIDs, subscriptionDetailIDs }),
      },
    );

    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(responseText);
    }

    return await response.json();
  } catch (error) {
    console.error("Error processing Omni orders:", error);
    throw error;
  }
};

export const refreshVendorData = async (): Promise<boolean> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const processResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/Vendor/RefreshVendorData`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!processResponse.ok) {
    console.error("Failed to refresh vendor data.");
    return false;
  }

  return true;
};

export function getMessageForVendorRequest(response: VendorResponse) {
  var message = "";
  if (response.errors.length > 0 && response.details.length === 0) {
    message = "Failed:\n" + response.errors.join("\n");
  } else if (response.errors.length > 0 && response.details.length > 0) {
    message =
      `Some orders successfully sent to InformData.\n Failed:\n` +
      response.errors.join("\n");
  } else {
    message = "All orders successfully sent to InformData.";
  }

  return message;
}
